
import {  useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import type { IMenu } from '@/interfaces/IMenu'
import useDarkMode from '@/hooks/useDarkMode'
import ModalSearch from 'components/ModalSearch'
import { Icon } from '@/assets/icons'
import {
  IconSearch,
  IconMoon,
  IconSun,
  IconBrandYoutube,
  IconBrandFacebook,
  IconBrandTiktok,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconX,
} from '@tabler/icons-react'

const NavbarCompact = () => {
  const modalSearchRef = useRef<{ openModal: () => void }>(null)
  const { theme, setTheme } = useDarkMode()
  const [darkMode, setDarkMode] = useState(theme === 'dark')
  const [menuOpen, setMenuOpen] = useState(false)
  const [heroColor, setHeroColor] = useState<string | null>(null)
  const [isHeroLight, setIsHeroLight] = useState(true)
  const location = useLocation()
  const isHome = location.pathname === '/'

  // Watch CSS custom properties set by HeroMotion
  const updateColor = useCallback(() => {
    const root = document.documentElement
    const r = root.style.getPropertyValue('--navbar-r')
    const g = root.style.getPropertyValue('--navbar-g')
    const b = root.style.getPropertyValue('--navbar-b')
    const light = root.style.getPropertyValue('--navbar-light')
    if (r && g && b) {
      setHeroColor(`rgba(${r}, ${g}, ${b}, 0.85)`)
      setIsHeroLight(light === '1')
    } else {
      setHeroColor(null)
    }
  }, [])

  useEffect(() => {
    if (!isHome) {
      setHeroColor(null)
      return
    }
    // Poll briefly for the CSS variable (set asynchronously by HeroMotion)
    updateColor()
    const observer = new MutationObserver(updateColor)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })
    return () => observer.disconnect()
  }, [isHome, updateColor])

  const toggleDarkMode = (checked: boolean) => {
    setDarkMode(checked)
    setTheme(checked ? 'dark' : 'light')
  }

  // MENUS
  const moviesMenu: IMenu = {
    type: 'movie',
    label: 'All Movies',
    href: '/movie/blue-fox-canada',
    menus: [
      { href: '/movie/domestic', label: 'Domestic' },
      { href: '/movie/international', label: 'International' },
      { href: '/movie/redhound-entertainment', label: 'Red Hound Entertainment' },
      { href: '/movie/blue-fox-entertainment', label: 'Blue Fox Entertainment' },
    ],
  }
  const financingMenu: IMenu = {
    type: 'financing',
    label: 'Financing',
    href: 'https://bluefoxfinancing.com',
    menus: [],
  }
  const canada: IMenu = {
    type: 'canada',
    label: 'Canada',
    href: '/movie/blue-fox-canada',
    menus: [],
  }
  const international: IMenu = {
    type: 'international',
    label: 'International',
    href: '/festivals',
    menus: [],
  }
  const newReleases: IMenu = { type: 'new-releases', label: 'New Releases', href: '/movie/new-releases', menus: [] }
  const shop: IMenu = { type: 'shop', label: 'Shop', href: 'https://unobstructedview.com', menus: [] }

  const YT_URL = 'https://www.youtube.com/channel/UCmHYPCM_h8Tw9JkI3UnrCvA'

  // turn desktop links on only at lg+ to "convert to mobile sooner"
  const desktopLinkCls = ({ isActive }: { isActive: boolean }) =>
    `px-2 py-1.5 rounded-sm text-sm font-medium transition
     ${isActive
      ? 'text-blue-700 dark:text-blue-300 bg-slate-900/5 dark:bg-white/10'
      : 'text-slate-700 dark:text-slate-200 hover:bg-slate-900/5 dark:hover:bg-white/10'
    }`

  return (
    <header className="fixed top-0 inset-x-0 z-[100] flex justify-center pt-3 px-3">
      <div
        className={`
          w-full max-w-sm sm:max-w-md
          h-9
          rounded-sm border
          backdrop-blur-md shadow-lg
          ${menuOpen ? 'invisible' : ''}
          flex items-center justify-between px-3
          transition-colors duration-700
          ${heroColor
            ? (isHeroLight ? 'border-black/10' : 'border-white/10')
            : 'border-slate-200/80 dark:border-white/10 bg-white/95 dark:bg-slate-900/90'
          }
        `}
        style={heroColor ? { backgroundColor: heroColor } : undefined}
      >
          {/* Left: hamburger + logo */}
          <div className="flex items-center gap-2">
            <button
              className={`inline-flex items-center justify-center transition
               ${heroColor
                 ? (isHeroLight ? 'text-slate-900' : 'text-white')
                 : 'text-slate-700 dark:text-slate-200'
               }`}
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M4 7h16v2H4zM4 11h16v2H4zM4 15h16v2H4z" />
              </svg>
            </button>

            <NavLink to="/" className="flex items-center gap-1.5">
              <img src={Icon} alt="Blue Fox" className={`w-6 h-6 ${isHome ? 'brightness-0' : 'brightness-0 dark:brightness-0 dark:invert'}`} />
              <span className={`text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap ${
                heroColor
                  ? (isHeroLight ? 'text-slate-900' : 'text-white')
                  : 'text-slate-900 dark:text-slate-100'
              }`}>
                Blue Fox Entertainment Canada
              </span>
            </NavLink>
          </div>

          {/* Right: icons */}
          {(() => {
            const iconCls = `inline-grid place-items-center transition ${
              heroColor
                ? (isHeroLight ? 'text-slate-900' : 'text-white')
                : 'text-slate-700 dark:text-slate-200'
            }`
            return (
              <div className="flex items-center gap-2">
                <button onClick={() => modalSearchRef.current?.openModal()} className={iconCls} aria-label="Search">
                  <IconSearch size={14} />
                </button>
                <button onClick={() => toggleDarkMode(!darkMode)} className={iconCls} aria-label="Toggle dark mode">
                  {darkMode ? <IconSun size={14} /> : <IconMoon size={14} />}
                </button>
              </div>
            )
          })()}
      </div>

      {/* Dropdown menu */}
      {menuOpen && (() => {
        const textCls = heroColor
          ? (isHeroLight ? 'text-slate-900' : 'text-white')
          : 'text-slate-900 dark:text-slate-100'
        const hoverTextCls = heroColor
          ? (isHeroLight ? 'hover:text-black' : 'hover:text-white/70')
          : 'hover:text-blue-600 dark:hover:text-blue-400'
        const iconCls = `inline-grid place-items-center transition ${textCls}`
        const socialCls = heroColor
          ? (isHeroLight ? 'text-slate-700 hover:text-slate-900' : 'text-white/60 hover:text-white')
          : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'

        return (
          <div className="fixed inset-0 z-[110]" onClick={() => setMenuOpen(false)}>
            <div
              className={`absolute top-3 left-1/2 -translate-x-1/2
                w-full max-w-sm sm:max-w-md
                rounded-sm shadow-2xl border overflow-hidden
                animate-[slideDown_200ms_ease-out] origin-top
                ${heroColor
                  ? (isHeroLight ? 'border-black/10' : 'border-white/10')
                  : 'border-slate-200/80 dark:border-white/10 bg-white dark:bg-slate-900'
                }`}
              style={heroColor ? { backgroundColor: heroColor } : undefined}
              onClick={(e) => e.stopPropagation()}
            >
              <style>{`
                @keyframes slideDown {
                  from { opacity: 0; transform: translateX(-50%) scaleY(0.95) translateY(-4px); }
                  to   { opacity: 1; transform: translateX(-50%) scaleY(1) translateY(0); }
                }
              `}</style>

              {/* Top bar */}
              <div className="flex items-center justify-between px-3 h-9">
                <div className="flex items-center gap-3">
                  <button className={iconCls} onClick={() => setMenuOpen(false)} aria-label="Close menu">
                    <IconX size={16} />
                  </button>
                  <NavLink to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-1.5">
                    <img src={Icon} alt="Blue Fox" className={`w-6 h-6 ${isHome ? 'brightness-0' : 'brightness-0 dark:brightness-0 dark:invert'}`} />
                    <span className={`text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap ${textCls}`}>
                      Blue Fox Entertainment Canada
                    </span>
                  </NavLink>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => { setMenuOpen(false); modalSearchRef.current?.openModal() }} className={iconCls} aria-label="Search">
                    <IconSearch size={15} />
                  </button>
                  <button onClick={() => toggleDarkMode(!darkMode)} className={iconCls} aria-label="Toggle dark mode">
                    {darkMode ? <IconSun size={15} /> : <IconMoon size={15} />}
                  </button>
                </div>
              </div>

              {/* Nav links */}
              <nav className="px-3 pt-3 pb-2 space-y-0.5">
                <NavLink to="/" onClick={() => setMenuOpen(false)}
                  className={`block py-1.5 text-sm font-semibold transition ${textCls} ${hoverTextCls}`}>
                  Home
                </NavLink>
                <NavLink to={newReleases.href!} onClick={() => setMenuOpen(false)}
                  className={`block py-1.5 text-sm font-semibold transition ${textCls} ${hoverTextCls}`}>
                  {newReleases.label}
                </NavLink>
                <NavLink to={moviesMenu.href!} onClick={() => setMenuOpen(false)}
                  className={`block py-1.5 text-sm font-semibold transition ${textCls} ${hoverTextCls}`}>
                  {moviesMenu.label}
                </NavLink>
                <a href={shop.href!} target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}
                  className={`block py-1.5 text-sm font-semibold transition ${textCls} ${hoverTextCls}`}>
                  {shop.label}
                </a>
              </nav>

              {/* Social icons */}
              <div className="px-3 pb-3 pt-1.5 flex items-center gap-3">
                <a href="https://www.instagram.com/bluefoxca/" target="_blank" rel="noreferrer" aria-label="Instagram" className={`transition ${socialCls}`}><IconBrandInstagram size={16} /></a>
                <a href="https://www.tiktok.com/@bluefoxentertainment" target="_blank" rel="noreferrer" aria-label="TikTok" className={`transition ${socialCls}`}><IconBrandTiktok size={16} /></a>
                <a href="https://www.facebook.com/profile.php?id=61575922139066" target="_blank" rel="noreferrer" aria-label="Facebook" className={`transition ${socialCls}`}><IconBrandFacebook size={16} /></a>
                <a href={YT_URL} target="_blank" rel="noreferrer" aria-label="YouTube" className={`transition ${socialCls}`}><IconBrandYoutube size={16} /></a>
                <a href="https://www.linkedin.com/company/blue-fox-entertainment/" target="_blank" rel="noreferrer" aria-label="LinkedIn" className={`transition ${socialCls}`}><IconBrandLinkedin size={16} /></a>
              </div>
            </div>
          </div>
        )
      })()}

      <ModalSearch ref={modalSearchRef} />
    </header>
  )
}

export default NavbarCompact
